import { action } from '@ember/object';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { Service, ValidationErrors } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';

import type { Args as BoardsShareScheduleBaseArgs } from 'webapp/components/boards/share/schedule/base';
import BoardsShareScheduleBaseComponent from 'webapp/components/boards/share/schedule/base';
import BoardsShareScheduleFormComponent from 'webapp/components/boards/share/schedule/form';
import SubmitButtonComponent from 'webapp/components/submit-button';

export interface Args extends BoardsShareScheduleBaseArgs {
	isContentShown: boolean;
}

export default class BoardsShareSendTabComponent extends BoardsShareScheduleBaseComponent<Args> {
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('analytics') declare analyticsService: Service.Analytics;

	@tracked submitInProgress = false;

	@tracked errors: ValidationErrors<'deliveryOrRecipients' | 'target'> = {};

	@action
	save() {
		const model = this.shareModel;

		if (!this.submitInProgress && model.validate() && this.validate()) {
			model.cleanupUnusedScheduleValues();

			this.submitInProgress = true;
			const layer = this.infolayerService.loading('Saving...');

			const trackData = model.getTrackingDataForCreate(this.args.trackingOrigin);

			this.analyticsService.event(TrackingEvent.SHARE_SNAPSHOT_NOW, trackData);

			model
				.save()
				.then(
					() => {
						layer.ok('Your snapshot has been sent!');
						next(this, this.setBlankShareModel);
					},
					(errorData) => {
						layer.close();
						throw errorData;
					},
				)
				.finally(() => {
					this.errors = {};
					this.submitInProgress = false;
				})
				.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
		}
	}

	private validate() {
		const errors: ValidationErrors<'deliveryOrRecipients' | 'target'> = {};

		const model = this.shareModel;

		if (!(model.loopId || model.boardId)) {
			errors.target = [{ name: 'target', message: 'Must choose a dashboard or loop!' }];
		}

		if (!model.isEmailDelivery && !model.isSlackDelivery) {
			errors.deliveryOrRecipients = [
				{
					name: 'deliveryOrRecipients',
					message: 'At least one delivery type / recipients must be selected',
				},
			];
		}

		this.errors = errors;

		return Object.keys(errors).length === 0;
	}
	<template>
		<div class="content2 {{if @isContentShown 'content2--shown'}} send-now">

			<BoardsShareScheduleFormComponent
				@model={{this.shareModel}}
				@isPeriodic={{false}}
				@trackingOrigin={{@trackingOrigin}}
				@onContentChange={{@onContentChange}}
				@errors={{this.errors}}
				@submitInProgress={{this.submitInProgress}}
			/>

			<div class="controls2">
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Cancel"
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{@onClose}}
					/>
					<SubmitButtonComponent
						@text="Send now"
						@extraClass="save"
						@onTrigger={{this.save}}
						@submitInProgress={{this.submitInProgress}}
					/>
				</div>
			</div>

		</div>
	</template>
}
